import React from "react";
import { H2, InternalLink, P, Strong } from "../components/Prose";

// Figures are from the pl-gami-matches table as of 23 September 2026.
export default function WhatIsScorigami() {
  return (
    <>
      <H2>Where the word comes from</H2>
      <P>
        The term was coined by the writer Jon Bois for American football, where
        teams score in awkward chunks of three and seven and genuinely new final
        scores still turn up most seasons. The idea travels well: take every
        result a competition has ever produced, plot them on a grid, and look
        for the squares that are still empty. Each empty square is a result
        waiting to happen for the first time.
      </P>

      <H2>Why football is different</H2>
      <P>
        Football is a low-scoring game, so its grid is small. Across{" "}
        <Strong>13,216 Premier League matches</Strong> since August 1992, only{" "}
        <Strong>36 different final scores</Strong> have ever been recorded, if
        you ignore which side was at home. Four results account for more than
        half of every match played: 1–0 (2,324 times), 2–1 (1,984), 2–0 (1,662)
        and 1–1 (1,521).
      </P>
      <P>
        At the other end, six scorelines have happened exactly once: 5–5, 6–4,
        7–3, 7–4, 8–2 and 9–1. Those are the league's classic scorigamis, and the
        most recent arrived on 19 May 2013, when West Brom and Manchester United
        drew 5–5 in Sir Alex Ferguson's final match. The stories behind all six
        are in{" "}
        <InternalLink to="/articles/rarest-premier-league-scorelines">
          the rarest scorelines in Premier League history
        </InternalLink>
        .
      </P>

      <H2>How to read the grid</H2>
      <P>
        On the <InternalLink to="/">home page</InternalLink>, every square is a
        final score. By default the horizontal axis is the home team's goals and
        the vertical axis is the away team's, so a 3–1 home win and a 1–3 away
        win are different squares. The number in each square is how many times
        that result has happened, and the colour bands group squares from
        "Once" up to "1,000+". Empty squares are scorelines the Premier League
        has never produced.
      </P>
      <P>
        Tap any square to see the first time it happened, the most recent time,
        and how often it has cropped up season by season. The season picker
        narrows the grid to a single campaign, from 1992/93 to the current one.
      </P>

      <H2>Two ways to count</H2>
      <P>
        The classic scorigami count ignores venue: 3–1 and 1–3 are the same
        result. Switch the grid to <Strong>Winner / Loser</Strong> to see it that
        way, with each of the 36 scorelines shown on both sides of the diagonal.
        The <Strong>Home / Away</Strong> view splits every result by venue,
        which turns 36 scorelines into 60 and brings the empty squares much
        closer to the action.
      </P>
      <P>
        Home advantage is visible in the shape of the grid. Home teams have won
        45.6% of Premier League matches and away teams 28.7%, with 25.7% drawn.
        That is why the away half of the grid thins out so much sooner than the
        home half.
      </P>

      <H2>Where the next one could come from</H2>
      <P>
        Counting home and away separately, the Premier League has produced three
        brand-new results since 2019, all of them away demolitions: Southampton
        0–9 Leicester City (25 October 2019), Crystal Palace 0–7 Liverpool (19
        December 2020) and Sheffield United 0–8 Newcastle (24 September 2023).
        The likeliest candidate for the next one is an away 7–2 win. It has never
        happened, even though 7–2 has been a home result five times.{" "}
        <InternalLink to="/articles/home-away-scorigami">
          Here is why the home/away view is where new scorigamis will come from
        </InternalLink>
        .
      </P>
    </>
  );
}
